import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { useHistory } from 'react-router-dom';
// import { MenuList, Dropdown, MenuListItem } from '@utmost.co/utmost-design-system';
import { IconButton, Icon, MenuList, Dropdown, MenuItem as MenuListItem } from '@beeline.com/bp-design-system';
import { useMixpanel } from 'src/integrations/mixpanel/useMixpanel';
import { MixpanelEvents } from 'src/integrations/mixpanel/Events/MixpanelEvents';
import { PagePathname } from 'src/utils/hooks/navigation/types';
import { useExportsActions } from 'src/utils/hooks/data/exports/useExportsActions';
import { queryStringStringify } from 'src/utils/queryString';
import { useNotificationsIntl } from '../translations/useNotificationsIntl';
import { useNotificationMapper } from './hooks/useNotificationMapper';
import { MappedNotification } from './Notifications.types';
import { useNotificationDownload } from './hooks/useNotificationDownload';
import {
  checkIsExportType,
  checkIsNotInProgress,
  getExecutionId,
  checkIsFailed,
  isNotSupportedExportType
} from './hooks/Notification.utils';

interface Props {
  notification: MappedNotification;
  handleClose: () => void;
  removeDismiss?: boolean;
  source: 'recent-exports' | 'notifications';
}

export enum NotificationActionType {
  CANCEL = 'notification-cancel',
  DOWNLOAD = 'notification-download',
  DISMISS = 'notification-dismiss',
  VIEW_DETAILS = 'notification-view-details',
  TRY_AGAIN = 'notification-try-again'
}

export const NotificationActions: FC<React.PropsWithChildren<Props>> = ({
  notification,
  handleClose,
  removeDismiss,
  source
}) => {
  const { track } = useMixpanel();
  const onDownload = useNotificationDownload();
  const { deleteNotifications } = useNotificationMapper();
  const intl = useNotificationsIntl();
  const { cancelExport, restartExport } = useExportsActions();
  const history = useHistory();

  const executionUid = getExecutionId(notification);
  const isNotInProgress = checkIsNotInProgress(notification.status);
  const isJobFailed = checkIsFailed(notification.status);
  const notificationTo = notification.to as PagePathname;
  const isExportType = checkIsExportType(notification);
  const isNotSupportedExport = isNotSupportedExportType(notification);

  return (
    <StyledActions>
      {notification.resourceLocation && (
        <IconButton
          dataTest={NotificationActionType.DOWNLOAD}
          iconName="cloud_download"
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            onDownload(notification, source);
            const startTime = notification.metadata?.find((item) => item?.key === 'START_TIME');
            const endTime = notification.metadata?.find((item) => item?.key === 'END_TIME');
            const requestedTime = notification.metadata?.find((item) => item?.key === 'REQUESTED_TIME');
            track(MixpanelEvents.myContentExport.DOWNLOAD_EXPORT, {
              executionId: executionUid,
              startTime,
              endTime,
              requestedTime
            });
          }}
        />
      )}
      <Dropdown
        ButtonItem={() => (
          <IconButton
            iconName="more_vert"
            dataTest="notification-actions-dropdown"
            onClick={(event) => event.preventDefault()}
            size="small"
          />
        )}
      >
        <MenuList>
          {notificationTo && !isNotSupportedExport && (
            <StyledMenuListItem
              dataTest={NotificationActionType.VIEW_DETAILS}
              onClick={() => {
                if (isExportType) {
                  track(
                    source === 'recent-exports'
                      ? MixpanelEvents.myContentExport.GO_TO_EXPORT_PANEL_FROM_RECENT_EXPORTS
                      : MixpanelEvents.myContentExport.GO_TO_EXPORT_PANEL_FROM_NOTIFICATION
                  );
                  // Pass extra state to force reload of Table in the Export Hub (MyContent/Export.tsx)
                  history.push(
                    `${notificationTo}?${queryStringStringify({
                      query: JSON.stringify({ search: executionUid })
                    })}`,
                    { executionUid }
                  );
                } else {
                  history.push(notificationTo);
                }

                handleClose();
              }}
            >
              <Icon name="info" size="small" />
              <span>{intl.viewDetails}</span>
            </StyledMenuListItem>
          )}
          {isExportType && !isNotInProgress && executionUid && !isNotSupportedExport && (
            <StyledMenuListItem dataTest={NotificationActionType.CANCEL} onClick={() => cancelExport(executionUid)}>
              <Icon name="cancel" size="small" />
              <span>{intl.cancel}</span>
            </StyledMenuListItem>
          )}
          {isExportType && isJobFailed && executionUid && !isNotSupportedExport && (
            <StyledMenuListItem dataTest={NotificationActionType.TRY_AGAIN} onClick={() => restartExport(executionUid)}>
              <Icon name="refresh" size="small" />
              <span>{intl.exportTryAgain}</span>
            </StyledMenuListItem>
          )}
          {isNotInProgress && !removeDismiss && (
            <StyledMenuListItem
              $alert
              dataTest={NotificationActionType.DISMISS}
              onClick={() => deleteNotifications(notification.uid)}
            >
              <Icon name="notifications_off" size="small" />
              <span>{intl.dismiss}</span>
            </StyledMenuListItem>
          )}
        </MenuList>
      </Dropdown>
    </StyledActions>
  );
};

const alertColor = css`
  span {
    color: ${({ theme }) => theme.colorAlert};
  }
  svg {
    fill: ${({ theme }) => theme.colorAlert};
  }
`;

const StyledActions = styled.div`
  .MuiButton-root {
    min-width: 32px;
  }
  display: flex;
  gap: ${({ theme }) => theme.spacingSize.m};
  padding-right: ${({ theme }) => theme.spacingSize.s};
`;

const StyledMenuListItem = styled(MenuListItem)<{ $alert?: boolean }>`
  display: flex;
  gap: ${({ theme }) => theme.spacingSize.s};
  ${({ $alert }) => ($alert ? alertColor : '')}
`;
